import React, { useEffect, useState } from 'react';
import { Award, Lock, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { fetchMyStats } from '../../lib/meApi';
import LevelAvatar from './LevelAvatar';

const GROUP_LABELS = {
  explorer:  'Exploración',
  collector: 'Colección',
  planner:   'Planificación',
  social:    'Social',
  curator:   'Curaduría',
  onboard:   'Inicio',
};

const BadgeTile = ({ b }) => {
  const earned = b.earned;
  const pct = Math.min(100, Math.round((b.progress / b.threshold) * 100));
  return (
    <div
      className={`relative rounded-2xl border p-3 transition-all ${
        earned
          ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200'
          : 'bg-white border-gray-200 opacity-90 hover:opacity-100'
      }`}
      data-testid={`badge-tile-${b.code}`}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center text-2xl shrink-0 ${
            earned ? 'bg-emerald-100' : 'bg-gray-100 grayscale opacity-60'
          }`}
        >
          {earned ? b.emoji : <Lock className="w-4 h-4 text-gray-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm truncate ${earned ? 'text-[#064E3B]' : 'text-gray-500'}`}>
            {b.name}
          </p>
          <p className="text-[11px] text-gray-500 leading-tight mt-0.5 line-clamp-2">{b.description}</p>
          {!earned && (
            <div className="mt-2">
              <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                <span>{b.progress}/{b.threshold}</span>
                <span>+{b.points} pts</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-400 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}
          {earned && (
            <span className="text-[10px] text-emerald-600 font-semibold mt-1 inline-block">+{b.points} pts</span>
          )}
        </div>
      </div>
    </div>
  );
};


const BadgesTab = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetchMyStats().then(setStats).catch(() => setStats(null));
  }, []);
  if (!stats) return <div className="text-center py-12 text-gray-400">Cargando…</div>;

  const u = stats.user;
  const groupOrder = ['explorer', 'planner', 'collector', 'social', 'curator', 'onboard'];
  const grouped = groupOrder.map(g => ({
    key: g,
    label: GROUP_LABELS[g],
    badges: stats.badges.filter(b => b.group === g),
  })).filter(g => g.badges.length);

  const pctToNext = u.level_max
    ? Math.min(100, Math.round(((u.points - u.level_min) / (u.level_max - u.level_min)) * 100))
    : 100;

  return (
    <div className="space-y-6" data-testid="me-badges-content">
      {/* Hero card with level */}
      <div className="rounded-3xl p-5 md:p-6 bg-gradient-to-br from-[#064E3B] via-[#065F46] to-[#047857] text-white shadow-lg">
        <div className="flex items-center gap-4">
          <LevelAvatar
            name={u.name}
            color={u.avatar_color}
            avatarUrl={u.avatar_url}
            level={u.level}
            size="xl"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-emerald-200/80">Nivel {u.level}</p>
            <h2 className="text-2xl md:text-3xl font-extrabold leading-tight">{u.level_name}</h2>
            <p className="text-xs text-emerald-100/80 mt-1">@{u.username}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-3xl md:text-4xl font-extrabold">{u.points}</p>
            <p className="text-[11px] uppercase tracking-wider text-emerald-200/80">PlanPoints</p>
          </div>
        </div>
        {u.level_max && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-emerald-100/80 mb-1.5">
              <span>{u.points} / {u.level_max}</span>
              <span>{u.level_max - u.points} pts al siguiente nivel</span>
            </div>
            <div className="h-2.5 bg-white/15 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-300 to-emerald-200 transition-all"
                style={{ width: `${pctToNext}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      <Card className="rounded-2xl border-0 shadow-sm bg-white">
        <CardContent className="p-5 flex items-center gap-4">
          <Award className="w-10 h-10 text-emerald-500" />
          <div>
            <p className="text-lg font-bold text-[#064E3B]">{stats.earned_count} / {stats.badge_total}</p>
            <p className="text-xs text-gray-500">Logros desbloqueados</p>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            <TrendingUp className="w-4 h-4" /> Sigue explorando para subir de nivel
          </div>
        </CardContent>
      </Card>

      {/* Groups */}
      {grouped.map(g => (
        <div key={g.key}>
          <h3 className="font-bold text-[#064E3B] text-sm uppercase tracking-wide mb-3 px-1">{g.label}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {g.badges.map(b => <BadgeTile key={b.code} b={b} />)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BadgesTab;
